import React, { createContext, useContext, useState, ReactNode } from 'react';
import { IPrintPageConfig } from '../components-ui/print-page/PrintPage';
import PrintDialog from '../components-ui/print-dialog/PrintDialog';

interface IPrintDialogOptions {
  title?: string;
  htmlContent: string;
  config?: IPrintPageConfig;
  onPrint?: () => void;
  onSendEmail?: () => void;
}

interface IPrintDialogContextValue {
  showPrintDialog: (options: IPrintDialogOptions) => void;
  hidePrintDialog: () => void;
}

const PrintDialogContext = createContext<IPrintDialogContextValue | undefined>(undefined);

export const usePrintDialog = () => {
  const context = useContext(PrintDialogContext);
  if (!context) {
    throw new Error('usePrintDialog must be used within PrintDialogProvider');
  }
  return context;
};

interface IPrintDialogProviderProps {
  children: ReactNode;
}

export const PrintDialogProvider: React.FC<IPrintDialogProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<IPrintDialogOptions | null>(null);

  const showPrintDialog = (options: IPrintDialogOptions) => {
    setDialogOptions(options);
    setIsOpen(true);
  };

  const hidePrintDialog = () => {
    setIsOpen(false);
    setDialogOptions(null);
  };

  return (
    <PrintDialogContext.Provider value={{ showPrintDialog, hidePrintDialog }}>
      {children}
      {isOpen && dialogOptions && (
        <PrintDialog
          isOpen={isOpen}
          onClose={hidePrintDialog}
          options={dialogOptions}
        />
      )}
    </PrintDialogContext.Provider>
  );
};